import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import cx from 'classnames';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useIsRTL } from '../../../locale';
import { postfixTestId } from '../../../util/testId';
import { InvisibleText } from '../../InvisibleText';
import { GlobalSettingsKeys, useGetGlobalSetting } from '../../Settings';
import { useSliderDrag } from '../useSliderDrag';
import cn from './SliderValueGrip.css.json';
import './SliderValueGrip.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('sliderGrip');
export const SliderGrip = /*#__PURE__*/forwardRef(function SliderGrip(props, ref) {
  const {
    startDrag,
    didDrag
  } = useSliderDrag({
    clientBounds: props.clientBounds,
    orientation: props.orientation,
    minPosition: props.scale.position(props.minValue),
    maxPosition: props.scale.position(props.maxValue),
    onChange: props.onChange,
    onCommit: props.onCommit
  });

  // Check if a custom focus indicator is set. If so, we'll display the focus indicator
  // regardless of mouse/keyboard interaction
  const customFocusIndicator = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';
  const [enableFocusOutline, setEnableFocusOutline] = useState(true);
  const clsName = cx(props.disabled && cn.disabled, props.orientation === 'vertical' ? cn.vertical : cn.horizontal, props.arrowGrips ? cn.arrowgrip : '');
  const isRTL = useIsRTL();
  const isEnabledSingleScale = props.enableSliderWithSingleScaleValue && props.scale.minValue === props.scale.maxValue;
  const position = isEnabledSingleScale ? 0.5 : props.scale.position(props.value);
  const style = {
    zIndex: props.zIndex,
    ...(props.orientation === 'vertical' // prettier
    ? {
      top: `${(1 - position) * 100}%`
    } : {
      [isRTL ? 'right' : 'left']: `${position * 100}%`
    })
  };
  const refGrip = useRef(null);
  const refGripContainer = useRef(null);
  useImperativeHandle(ref, () => ({
    focusGrip() {
      if (refGrip.current) refGrip.current.focus();
    },
    getGripElement() {
      return refGrip.current;
    },
    getGripContainerElement() {
      return refGripContainer.current;
    }
  }), [refGrip]);
  const htmlId = useHtmlId();
  const invisibleTextId = props.ariaLabel ? htmlId : undefined;
  const ariaLabelledBy = props.ariaLabelledBy && invisibleTextId ? `${props.ariaLabelledBy} ${invisibleTextId}` : props.ariaLabelledBy ?? invisibleTextId;
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": postfixTestId(props.testId, 'container'),
    className: cn.container[props.orientation],
    ref: refGripContainer,
    children: [props.ariaLabel && /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleTextId,
      testId: postfixTestId(props.testId, 'invisibleText'),
      children: props.ariaLabel
    }), /*#__PURE__*/_jsx("div", {
      "data-testid": props.testId,
      className: cx(enableFocusOutline && cn.gripEnableFocusOutline, cn.container[props.orientation], cn.containerGrip),
      style: style,
      ref: refGrip,
      role: "slider",
      tabIndex: 0,
      "aria-labelledby": ariaLabelledBy,
      "aria-orientation": props.orientation,
      "aria-valuetext": props.formattedValue,
      "aria-valuenow": props.value,
      "aria-valuemin": props.minValue,
      "aria-valuemax": props.maxValue,
      "aria-disabled": props.disabled,
      "aria-describedby": props.ariaDescribedBy
      // NOVACOMMONS-8942: don't set aria-readonly if the slider is disabled
      ,
      "aria-readonly": props.disabled ? undefined : props.readOnly,
      onMouseDown: props.disabled || props.readOnly ? undefined : e => {
        // only process left clicks
        if (e.button !== 0) return;
        // need to prevent default action, which starts a native DOM drag-and-drop sometimes.
        e.preventDefault();
        startDrag([e.clientX, e.clientY], position);
        if (refGrip.current) {
          // focus the grip so it can receive keyboard input and closes any grip inputs
          refGrip.current.focus();
        }
        if (!customFocusIndicator) {
          // don't show focus outline when the user drags a grip
          setEnableFocusOutline(false);
        }
        props.onDragStart && props.onDragStart();
      },
      onClick: props.disabled || props.readOnly ? undefined : () => {
        if (!didDrag && props.onClick) {
          props.onClick();
        }
      },
      onKeyDown: props.disabled || props.readOnly ? undefined : e => {
        const handleKeyAction = action => {
          // NOTE: each of these keys could cause scrolling to occur, so we preventDefault in those cases.
          e.preventDefault();
          // show focus outline when the user uses the keyboard
          setEnableFocusOutline(true);
          props.onKeyAction && props.onKeyAction(action);
        };
        switch (e.key) {
          case 'ArrowRight':
            handleKeyAction({
              type: e.shiftKey ? 'stepTwo' : 'step',
              dir: isRTL ? 'backward' : 'forward'
            });
            break;
          case 'ArrowUp':
            handleKeyAction({
              type: e.shiftKey ? 'stepTwo' : 'step',
              dir: 'forward'
            });
            break;
          case 'ArrowLeft':
            handleKeyAction({
              type: e.shiftKey ? 'stepTwo' : 'step',
              dir: isRTL ? 'forward' : 'backward'
            });
            break;
          case 'ArrowDown':
            handleKeyAction({
              type: e.shiftKey ? 'stepTwo' : 'step',
              dir: 'backward'
            });
            break;
          case 'Home':
            handleKeyAction({
              type: 'stepMax',
              dir: 'backward'
            });
            break;
          case 'End':
            handleKeyAction({
              type: 'stepMax',
              dir: 'forward'
            });
            break;
          case 'PageUp':
            handleKeyAction({
              type: 'stepLarge',
              dir: 'forward'
            });
            break;
          case 'PageDown':
            handleKeyAction({
              type: 'stepLarge',
              dir: 'backward'
            });
            break;
        }
      },
      onBlur: props.disabled || props.readOnly ? undefined : () => {
        // reset focus, so if a user tabs into the slider grip it'll show an outline
        setEnableFocusOutline(true);
      },
      children: /*#__PURE__*/_jsxs("div", {
        "data-testid": postfixTestId(props.testId, 'inner'),
        className: clsName,
        children: [props.inclusive ? /*#__PURE__*/_jsx("div", {
          className: cx(props.arrowGrips ? cn.arrowIndicator : cn.indicator, props.disabled && cn.disabled)
        }) : null, props.children]
      })
    })]
  });
});
//# sourceMappingURL=SliderGrip.js.map